import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, TrendingUp, TrendingDown, Minus, AlertTriangle } from 'lucide-react';
import { apiFetch } from '../../../utils/api';
import { useToast } from '../../../components/ui/Toast';
import './inventory-tracker.css';

const formatMoney = (n) => `₦${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const formatNumber = (n) => Number(n || 0).toLocaleString();
const formatDate = (v) => {
  if (!v) return '—';
  return new Date(v).toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * Valuation tab — premium-gated. Live current-day valuation + trend
 * against the most recent stored snapshot + rolling 12-month history.
 * Backend gate returns 403 for non-premium; hook renders friendly
 * upgrade prompt on failure.
 */
const ValuationTab = () => {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setGated(false);
    try {
      const res = await apiFetch('/merchants/inventory/valuation');
      if (res.status === 403) {
        setGated(true);
        return;
      }
      const payload = await res.json();
      if (!res.ok || !payload.success) throw new Error(payload.error || 'Unable to load valuation');
      setData(payload);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const trendIcon = (delta) => {
    if (delta == null) return <Minus size={14} />;
    if (delta > 0) return <TrendingUp size={14} color="#166534" />;
    if (delta < 0) return <TrendingDown size={14} color="#b91c1c" />;
    return <Minus size={14} />;
  };

  return (
    <div className="itk-panel">
      <div className="itk-panel-header">
        <div>
          <h2>Inventory valuation</h2>
          <p>Monthly at-cost and at-retail snapshots for your books.</p>
        </div>
        <button type="button" className="itk-btn" onClick={load} disabled={loading}>
          {loading ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="itk-loading"><Loader2 size={18} className="spin" /> Loading valuation…</div>
      ) : gated ? (
        <div className="itk-upgrade">
          <strong>Premium feature</strong>
          <p>Track your inventory value over time and hand the accountant a clean at-cost snapshot each month.</p>
        </div>
      ) : !data ? null : (
        <>
          <div className="itk-metrics">
            <div className="itk-card">
              <p className="itk-card-title">Units on hand (today)</p>
              <div className="itk-card-value">{formatNumber(data.current.totalUnits)}</div>
              <div className="itk-card-caption">Live count from active batches</div>
            </div>
            <div className="itk-card">
              <p className="itk-card-title">At cost (today)</p>
              <div className="itk-card-value">{formatMoney(data.current.totalAtCost)}</div>
              {data.trend && (
                <div className="itk-card-caption" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  {trendIcon(data.trend.deltaAtCost)}
                  <span>
                    {data.trend.deltaAtCost >= 0 ? '+' : ''}{formatMoney(data.trend.deltaAtCost)}
                    {data.trend.deltaPercent != null && ` (${data.trend.deltaPercent}%)`} vs {formatDate(data.trend.asOfPrevious)}
                  </span>
                </div>
              )}
            </div>
            <div className="itk-card">
              <p className="itk-card-title">At retail (today)</p>
              <div className="itk-card-value">{formatMoney(data.current.totalAtRetail)}</div>
              <div className="itk-card-caption">Product price × qty remaining</div>
            </div>
          </div>

          {data.current.dataHygiene && data.current.dataHygiene.orphanUnits > 0 && (
            <section className="itk-card" style={{ borderColor: '#fbbf24', background: '#fffbeb' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <AlertTriangle size={20} color="#d97706" style={{ flexShrink: 0, marginTop: 2 }} />
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#78350f' }}>
                    Unsellable inventory detected
                  </h3>
                  <p style={{ margin: '4px 0 12px', fontSize: '0.85rem', color: '#78350f' }}>
                    {formatNumber(data.current.dataHygiene.orphanUnits)} units worth {formatMoney(data.current.dataHygiene.orphanAtCost)} sit in parent-slot batches on variant products — they can&apos;t be sold through any channel because sale paths require a specific variant. Ask ops to reconcile these batches (either assign them to a variant or write them off).
                  </p>
                  {data.current.dataHygiene.orphanProducts?.length > 0 && (
                    <div className="itk-scroll" style={{ borderColor: '#fcd34d' }}>
                      <table className="itk-table">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th className="num">Orphan units</th>
                            <th className="num">Orphan cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.current.dataHygiene.orphanProducts.map((op) => (
                            <tr key={op.productId}>
                              <td>{op.name}</td>
                              <td className="num">{formatNumber(op.units)}</td>
                              <td className="num">{formatMoney(op.atCost)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          <section className="itk-card">
            <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 600, color: '#111827' }}>Month-end snapshots</h3>
            {(data.history || []).length === 0 ? (
              <div className="itk-empty">
                <strong>No snapshots yet</strong>
                The first month-end snapshot is written automatically on the 1st of every month.
              </div>
            ) : (
              <div className="itk-scroll">
                <table className="itk-table">
                  <thead>
                    <tr>
                      <th>As of</th>
                      <th className="num">Units</th>
                      <th className="num">At cost</th>
                      <th className="num">At retail</th>
                      <th className="num">Aged &gt; 90d</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.history.map((row) => (
                      <tr key={row.id}>
                        <td>{formatDate(row.asOfDate)}</td>
                        <td className="num">{formatNumber(row.totalUnits)}</td>
                        <td className="num">{formatMoney(row.totalAtCost)}</td>
                        <td className="num">{formatMoney(row.totalAtRetail)}</td>
                        <td className="num" style={{ color: row.agedOver90Cost > 0 ? '#b91c1c' : '#6b7280' }}>
                          {formatMoney(row.agedOver90Cost)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default ValuationTab;
